import React, { useState } from 'react';
import { Upload, Send, AlertCircle } from 'lucide-react';

const ShareStory = () => {
  const [formData, setFormData] = useState({
    name: '',
    usePseudonym: false,
    pseudonym: '',
    location: '',
    storyType: 'personal',
    storyTitle: '',
    storyContent: '',
    file: null
  });

  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // This will be connected to backend later
    console.log('Form submitted:', formData);
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 5000);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({
      ...prev,
      file: e.target.files[0]
    }));
  };

  return (
    <div className="min-h-screen bg-[#1a1c1b] pt-20">
      {/* Hero */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="/images/wang-kelian-perlis.jpg"
            alt="Share Your Story"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a1c1b] via-[#1a1c1b]/90 to-[#1a1c1b]"></div>
        </div>
        
        <div className="container-custom relative z-10">
          <h1 className="heading-1 text-[#dc2626] mb-6 max-w-3xl">
            Share Your Story
          </h1>
          <p className="body-large text-white/90 max-w-3xl">
            Help preserve Malaysia's heritage by sharing stories, memories, or knowledge of forgotten sites.
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="space-section bg-[#0f0f10]">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="bg-[#302f2c] p-8 rounded-lg border-l-4 border-[#dc2626] mb-12">
              <h2 className="heading-3 text-[#dc2626] mb-4">Your Voice Matters</h2>
              <p className="body-medium text-white/90 mb-4">
                Local knowledge is invaluable in documenting Malaysia's dark tourism landscape. 
                Whether you've visited a forgotten site, heard a family story, or know about a place 
                fading from memory, we invite you to share it.
              </p>
              <p className="body-medium text-white/80">
                All submissions are reviewed before publication. We value history, heritage, and real 
                stories told with care and respect.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-12">
              <div className="bg-[#1a1c1b] p-6 rounded-lg border border-[#3a2222]">
                <h3 className="heading-4 text-[#dc2626] mb-3">What You Can Share</h3>
                <ul className="space-y-2 body-small text-white/70">
                  <li>• Personal experiences visiting dark tourism sites</li>
                  <li>• Family histories connected to historical events</li>
                  <li>• Knowledge of forgotten or neglected sites</li>
                  <li>• Community memories and oral histories</li>
                  <li>• Photographs of sites at risk of disappearing</li>
                </ul>
              </div>

              <div className="bg-[#1a1c1b] p-6 rounded-lg border border-[#3a2222]">
                <h3 className="heading-4 text-[#dc2626] mb-3">Our Commitment</h3>
                <ul className="space-y-2 body-small text-white/70">
                  <li>• Respectful handling of all submissions</li>
                  <li>• Option to remain anonymous or use pseudonym</li>
                  <li>• Review before publication</li>
                  <li>• Proper attribution and credit given</li>
                  <li>• Focus on preservation, not sensationalism</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Submission Form */}
      <section className="space-section bg-[#1a1c1b]">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="heading-2 text-[#dc2626] mb-8">Submit Your Story</h2>

            {submitted && (
              <div className="bg-[#dc2626] text-white p-6 rounded-lg mb-8 flex items-center gap-4">
                <AlertCircle size={24} />
                <div>
                  <p className="font-semibold">Thank you for your submission!</p>
                  <p className="text-sm">We'll review your story and be in touch soon.</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Name */}
              <div>
                <label className="block text-white font-semibold mb-3">
                  Your Name <span className="text-[#dc2626]">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full bg-[#302f2c] border border-[#3a2222] rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#dc2626] transition-colors"
                  placeholder="Enter your full name"
                />
              </div>

              {/* Pseudonym Option */}
              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="usePseudonym"
                    checked={formData.usePseudonym}
                    onChange={handleChange}
                    className="w-5 h-5 accent-[#dc2626]"
                  />
                  <span className="text-white">I prefer to use a pseudonym when published</span>
                </label>
                
                {formData.usePseudonym && (
                  <input
                    type="text"
                    name="pseudonym"
                    value={formData.pseudonym}
                    onChange={handleChange}
                    className="w-full bg-[#302f2c] border border-[#3a2222] rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#dc2626] transition-colors mt-3"
                    placeholder="Enter your preferred pseudonym"
                  />
                )}
              </div>

              {/* Location */}
              <div>
                <label className="block text-white font-semibold mb-3">
                  Location / Site Name <span className="text-[#dc2626]">*</span>
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  required
                  className="w-full bg-[#302f2c] border border-[#3a2222] rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#dc2626] transition-colors"
                  placeholder="e.g., Penang War Museum, Bukit Kepong, etc."
                />
              </div>

              {/* Story Type */}
              <div>
                <label className="block text-white font-semibold mb-3">
                  Story Type <span className="text-[#dc2626]">*</span>
                </label>
                <select
                  name="storyType"
                  value={formData.storyType}
                  onChange={handleChange}
                  required
                  className="w-full bg-[#302f2c] border border-[#3a2222] rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#dc2626] transition-colors"
                >
                  <option value="personal">Personal Experience / Visit</option>
                  <option value="forgotten">Forgotten / Neglected Site</option>
                  <option value="family">Family History / Memory</option>
                  <option value="community">Community Heritage</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Story Title */}
              <div>
                <label className="block text-white font-semibold mb-3">
                  Story Title <span className="text-[#dc2626]">*</span>
                </label>
                <input
                  type="text"
                  name="storyTitle"
                  value={formData.storyTitle}
                  onChange={handleChange}
                  required
                  className="w-full bg-[#302f2c] border border-[#3a2222] rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#dc2626] transition-colors"
                  placeholder="Give your story a title"
                />
              </div>

              {/* Story Content */}
              <div>
                <label className="block text-white font-semibold mb-3">
                  Your Story <span className="text-[#dc2626]">*</span>
                </label>
                <textarea
                  name="storyContent"
                  value={formData.storyContent}
                  onChange={handleChange}
                  required
                  rows="10"
                  className="w-full bg-[#302f2c] border border-[#3a2222] rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#dc2626] transition-colors resize-y"
                  placeholder="Share your story, memory, or information about the site. Please be as detailed as possible while maintaining respect for the subject matter."
                />
                <p className="text-[#888680] text-sm mt-2">Minimum 100 words recommended</p>
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-white font-semibold mb-3">
                  Upload Photo or Document (Optional)
                </label>
                <div className="border-2 border-dashed border-[#3a2222] rounded-lg p-8 text-center hover:border-[#dc2626] transition-colors">
                  <Upload className="mx-auto text-[#dc2626] mb-4" size={48} />
                  <p className="text-white mb-2">Click to upload or drag and drop</p>
                  <p className="text-[#888680] text-sm mb-4">PNG, JPG, PDF up to 10MB</p>
                  <input
                    type="file"
                    onChange={handleFileChange}
                    accept="image/*,.pdf"
                    className="hidden"
                    id="file-upload"
                  />
                  <label htmlFor="file-upload" className="btn-secondary text-sm cursor-pointer">
                    Choose File
                  </label>
                  {formData.file && (
                    <p className="text-[#dc2626] mt-4">Selected: {formData.file.name}</p>
                  )}
                </div>
              </div>

              {/* Privacy Notice */}
              <div className="bg-[#302f2c] p-6 rounded-lg border border-[#3a2222]">
                <h3 className="text-white font-semibold mb-3">Privacy & Review Process</h3>
                <ul className="space-y-2 body-small text-white/70">
                  <li>• All submissions are reviewed before publication</li>
                  <li>• Your contact information will not be published</li>
                  <li>• We may contact you for clarification or additional information</li>
                  <li>• You can request to remain anonymous or use a pseudonym</li>
                  <li>• We reserve the right to edit for clarity while maintaining your voice</li>
                </ul>
              </div>

              {/* Submit Button */}
              <button type="submit" className="btn-primary w-full text-lg">
                <Send className="mr-2" size={20} />
                Submit Your Story
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Why Share */}
      <section className="space-section bg-[#0f0f10]">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="heading-2 text-[#dc2626] mb-8">Why Your Story Matters</h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-[#1a1c1b] p-8 rounded-lg border border-[#3a2222]">
                <h3 className="heading-4 text-white mb-4">Preservation</h3>
                <p className="body-small text-white/70">
                  Your story helps preserve memories and histories that might otherwise be lost to time.
                </p>
              </div>

              <div className="bg-[#1a1c1b] p-8 rounded-lg border border-[#3a2222]">
                <h3 className="heading-4 text-white mb-4">Education</h3>
                <p className="body-small text-white/70">
                  Personal accounts provide invaluable educational resources for researchers and the public.
                </p>
              </div>

              <div className="bg-[#1a1c1b] p-8 rounded-lg border border-[#3a2222]">
                <h3 className="heading-4 text-white mb-4">Connection</h3>
                <p className="body-small text-white/70">
                  Stories create connections between people and places, fostering understanding and empathy.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ShareStory;
